import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Alert, Platform, ScrollView, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import SectionCard from '../components/SectionCard';
import DateInput from '../components/DateInput';
import SubExpenseEditor, { normalizeSubExpenses, subExpenseTotal } from '../components/SubExpenseEditor';
import { fetchCategories, fetchExpenseTemplates, fetchFundPartitions, postTransaction } from '../lib/api';
import { currency, todayKey } from '../lib/format';

const defaultCategories = {
  expense: ['Food', 'Transport', 'Rent', 'Utilities', 'Bills', 'Shopping', 'Health', 'Entertainment', 'Subscriptions', 'Other'],
  income: ['Salary', 'Business', 'Freelance', 'Investment', 'Gift', 'Refund', 'Other'],
};

const paymentMethods = ['Cash', 'Debit card', 'Credit card', 'Transfer', 'Mobile money', 'Other'];

export default function AddTransactionScreen({ navigation }) {
  const [type, setType] = useState('expense');
  const [categories, setCategories] = useState(defaultCategories);
  const [category, setCategory] = useState('Food');
  const [customCategory, setCustomCategory] = useState('');
  const [item, setItem] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayKey());
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [partitions, setPartitions] = useState([]);
  const [partitionId, setPartitionId] = useState('');
  const [subExpenses, setSubExpenses] = useState([]);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => setCategories(defaultCategories));
    fetchExpenseTemplates().then(setTemplates).catch(() => setTemplates([]));
    fetchFundPartitions().then(setPartitions).catch(() => setPartitions([]));
  }, []);

  const activeCategories = useMemo(() => categories[type] || defaultCategories[type], [categories, type]);
  const finalCategory = customCategory.trim() || category;
  const visibleTemplates = templates.filter((entry) => !customCategory && (!category || entry.category === category)).slice(0, 12);

  const selectType = (nextType) => {
    setType(nextType);
    setCategory((categories[nextType] || defaultCategories[nextType])[0]);
  };

  const showMessage = (title, message) => {
    if (Platform.OS === 'web') window.alert(`${title}\n\n${message}`);
    else Alert.alert(title, message);
  };

  const handleSave = async () => {
    const subRows = normalizeSubExpenses(subExpenses);
    const subTotal = subExpenseTotal(subRows);
    const numericAmount = Number(amount) || subTotal;
    if (!item.trim() || !finalCategory || !Number.isFinite(numericAmount) || numericAmount <= 0 || !date) {
      showMessage('Missing information', 'Please add an item/source, category, valid amount, and date.');
      return;
    }

    setSaving(true);
    try {
      await postTransaction({
        type,
        category: finalCategory,
        item: item.trim(),
        amount: numericAmount,
        date,
        notes,
        payment_method: paymentMethod,
        partition_id: type === 'expense' && partitionId ? Number(partitionId) : null,
        sub_expenses: type === 'expense' ? subRows : [],
      });
      navigation.navigate('Home');
    } catch (err) {
      showMessage('Could not save', err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ScrollView className="flex-1 bg-slate-50" contentContainerStyle={{ padding: 16, paddingBottom: 36 }}>
      <View className="rounded-2xl bg-white p-5 shadow-sm">
        <Text className="text-2xl font-black text-slate-950">Record money movement</Text>
        <Text className="mt-2 text-slate-500">Log income or spending with enough detail for daily, weekly, and monthly analysis.</Text>
      </View>

      <View className="mt-4">
        <SectionCard title="Transaction Type">
          <View className="flex-row gap-3">
            {[
              { key: 'expense', label: 'Expense', icon: 'trending-down' },
              { key: 'income', label: 'Income', icon: 'trending-up' },
            ].map((option) => (
              <TouchableOpacity
                key={option.key}
                className={`flex-1 rounded-2xl border p-4 ${type === option.key ? 'border-cyan-700 bg-cyan-50' : 'border-slate-200 bg-white'}`}
                onPress={() => selectType(option.key)}
              >
                <MaterialIcons name={option.icon} size={24} color={type === option.key ? '#0e7490' : '#64748b'} />
                <Text className={`mt-3 font-bold ${type === option.key ? 'text-cyan-800' : 'text-slate-700'}`}>{option.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title={type === 'expense' ? 'What did you spend on?' : 'Where did the money come from?'}>
          {type === 'expense' && visibleTemplates.length ? (
            <View className="mb-4">
              <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Quick expenses</Text>
              <View className="flex-row flex-wrap gap-2">
                {visibleTemplates.map((template) => (
                  <TouchableOpacity
                    key={template.id}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2"
                    onPress={() => {
                      setCategory(template.category);
                      setCustomCategory('');
                      setItem(template.item);
                      if (template.typical_amount) setAmount(String(template.typical_amount));
                      if (template.notes) setNotes(template.notes);
                    }}
                  >
                    <Text className="font-semibold text-slate-700">{template.item}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          ) : null}

          <TextInput
            value={item}
            onChangeText={setItem}
            className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-base text-slate-950"
            placeholder={type === 'expense' ? 'e.g. Lunch, Uber, rent, Netflix' : 'e.g. May salary, client invoice'}
            placeholderTextColor="#94a3b8"
          />

          <Text className="mt-5 text-sm font-bold uppercase text-slate-500">Category</Text>
          <View className="mt-3 flex-row flex-wrap gap-2">
            {activeCategories.map((entry) => (
              <TouchableOpacity
                key={entry}
                className={`rounded-full border px-4 py-2 ${category === entry && !customCategory ? 'border-cyan-700 bg-cyan-700' : 'border-slate-200 bg-white'}`}
                onPress={() => {
                  setCategory(entry);
                  setCustomCategory('');
                }}
              >
                <Text className={`font-semibold ${category === entry && !customCategory ? 'text-white' : 'text-slate-700'}`}>{entry}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <TextInput
            value={customCategory}
            onChangeText={setCustomCategory}
            className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-base text-slate-950"
            placeholder="Custom category"
            placeholderTextColor="#94a3b8"
          />
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Amount And Date">
          <View className="gap-3">
            <View>
              <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Amount</Text>
              <TextInput
                value={amount}
                onChangeText={setAmount}
                keyboardType="decimal-pad"
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-base text-slate-950"
                placeholder="0.00"
                placeholderTextColor="#94a3b8"
              />
            </View>
            <View>
              <Text className="mb-2 text-sm font-bold uppercase text-slate-500">Date</Text>
              <DateInput
                value={date}
                onChangeText={setDate}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-base text-slate-950"
                placeholder="Date"
              />
            </View>
          </View>
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Payment Method">
          <View className="flex-row flex-wrap gap-2">
            {paymentMethods.map((method) => (
              <TouchableOpacity
                key={method}
                className={`rounded-full border px-4 py-2 ${paymentMethod === method ? 'border-slate-900 bg-slate-900' : 'border-slate-200 bg-white'}`}
                onPress={() => setPaymentMethod(method)}
              >
                <Text className={`font-semibold ${paymentMethod === method ? 'text-white' : 'text-slate-700'}`}>{method}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </SectionCard>
      </View>

      {type === 'expense' ? (
        <View className="mt-4">
          <SubExpenseEditor
            rows={subExpenses}
            onChange={(rows) => {
              setSubExpenses(rows);
              const total = subExpenseTotal(normalizeSubExpenses(rows));
              if (total > 0) setAmount(String(total));
            }}
            title="Sub-expenses"
            subtitle="For example: Food Stuff can contain rice, beans, oil, and pepper."
          />
        </View>
      ) : null}

      {type === 'expense' ? (
        <View className="mt-4">
          <SectionCard title="Spend From Fund Partition" subtitle="Choose where this expense should come from, such as Transportation, Food, or Bills.">
            <View className="flex-row flex-wrap gap-2">
              <TouchableOpacity
                className={`rounded-full border px-4 py-2 ${!partitionId ? 'border-cyan-700 bg-cyan-700' : 'border-slate-200 bg-white'}`}
                onPress={() => setPartitionId('')}
              >
                <Text className={`font-semibold ${!partitionId ? 'text-white' : 'text-slate-700'}`}>General balance</Text>
              </TouchableOpacity>
              {partitions.filter((entry) => entry.active).map((partition) => (
                <TouchableOpacity
                  key={partition.id}
                  className={`rounded-full border px-4 py-2 ${String(partition.id) === String(partitionId) ? 'border-cyan-700 bg-cyan-700' : 'border-slate-200 bg-white'}`}
                  onPress={() => setPartitionId(String(partition.id))}
                >
                  <Text className={`font-semibold ${String(partition.id) === String(partitionId) ? 'text-white' : 'text-slate-700'}`}>
                    {partition.name} • {currency(partition.remaining_amount)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {!partitions.length ? <Text className="mt-3 text-sm text-slate-500">Create fund partitions on the Planning screen.</Text> : null}
          </SectionCard>
        </View>
      ) : null}

      <View className="mt-4">
        <SectionCard title="Notes">
          <TextInput
            value={notes}
            onChangeText={setNotes}
            multiline
            className="min-h-[100px] rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-base text-slate-950"
            placeholder="Optional details"
            placeholderTextColor="#94a3b8"
            textAlignVertical="top"
          />
        </SectionCard>
      </View>

      <TouchableOpacity className="mt-5 flex-row items-center justify-center rounded-2xl bg-cyan-700 px-5 py-4" onPress={handleSave} disabled={saving}>
        {saving ? <ActivityIndicator color="#ffffff" /> : <MaterialIcons name="save" size={21} color="#ffffff" />}
        <Text className="ml-2 text-base font-bold text-white">{saving ? 'Saving...' : `Save ${type}`}</Text>
      </TouchableOpacity>
    </ScrollView>
  );
}
